import { useMemo } from 'react';
import DOMPurify from 'dompurify';
import type { EmailResponse } from '@vagusx/clients';

interface EmailHtmlViewerProps {
  email: EmailResponse;
  /** Label provenance popover, rendered as an info icon next to the subject */
  labelProvenance?: React.ReactNode;
}

export function EmailHtmlViewer({
  email,
  labelProvenance,
}: EmailHtmlViewerProps): React.ReactElement {
  const sanitizedHtml = useMemo(() => {
    if (!email.bodyHtml) return null;

    return DOMPurify.sanitize(email.bodyHtml, {
      ALLOWED_TAGS: [
        'a',
        'abbr',
        'address',
        'b',
        'bdo',
        'blockquote',
        'br',
        'caption',
        'cite',
        'code',
        'col',
        'colgroup',
        'dd',
        'del',
        'details',
        'dfn',
        'div',
        'dl',
        'dt',
        'em',
        'figcaption',
        'figure',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'hr',
        'i',
        'img',
        'ins',
        'kbd',
        'li',
        'mark',
        'ol',
        'p',
        'pre',
        'q',
        'rp',
        'rt',
        'ruby',
        's',
        'samp',
        'small',
        'span',
        'strong',
        'sub',
        'summary',
        'sup',
        'table',
        'tbody',
        'td',
        'tfoot',
        'th',
        'thead',
        'time',
        'tr',
        'u',
        'ul',
        'var',
        'wbr',
        'font',
        'center',
        'style',
      ],
      ALLOWED_ATTR: [
        'href',
        'src',
        'alt',
        'title',
        'class',
        'id',
        'style',
        'target',
        'rel',
        'width',
        'height',
        'border',
        'cellpadding',
        'cellspacing',
        'colspan',
        'rowspan',
        'align',
        'valign',
        'bgcolor',
        'color',
        'face',
        'size',
        'dir',
        'lang',
      ],
      ALLOW_DATA_ATTR: false,
    });
  }, [email.bodyHtml]);

  // Metadata header
  const fromDisplay = email.fromName ? `${email.fromName} <${email.fromEmail}>` : email.fromEmail;
  const receivedDate = new Date(email.receivedAt).toLocaleString();

  return (
    <div className="flex flex-col">
      {/* Email header */}
      <div className="shrink-0 space-y-1 border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold">{email.subject}</h3>
          {labelProvenance && <div className="ml-auto shrink-0">{labelProvenance}</div>}
        </div>
        <p className="text-xs text-muted-foreground">From: {fromDisplay}</p>
        <p className="text-xs text-muted-foreground">Received: {receivedDate}</p>
      </div>

      {/* Email body */}
      <div className="p-4">
        {sanitizedHtml ? (
          <div
            className="prose prose-sm dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
          />
        ) : email.bodyText ? (
          <pre className="whitespace-pre-wrap font-sans text-sm text-foreground">
            {email.bodyText}
          </pre>
        ) : (
          <p className="text-sm text-muted-foreground">No email content available</p>
        )}
      </div>
    </div>
  );
}
